import { useEffect, useState } from "react";
import api from "../utils/api";
import { useAuth } from "../context/AuthContext";

const RetailersPage = () => {
  const { user } = useAuth();
  const canEdit = user?.role === "admin" || user?.role === "creator";
  const [retailers, setRetailers] = useState([]);
  const [showInactive, setShowInactive] = useState(false);
  const [showAdd, setShowAdd] = useState(false);
  const [editingRetailer, setEditingRetailer] = useState(null);
  const [newName, setNewName] = useState("");
  const [editName, setEditName] = useState("");
  const [editDesc, setEditDesc] = useState("");
  const [error, setError] = useState("");

  const fetchRetailers = async () => {
    try {
      const res = await api.get("/api/retailers?all=true");
      setRetailers(res.data);
    } catch (err) { console.error(err); }
  };

  useEffect(() => { fetchRetailers(); }, []);

  const filtered = retailers.filter((r) => showInactive || r.isActive);
  const inactiveCount = retailers.filter((r) => !r.isActive).length;

  const handleAdd = async () => {
    if (!newName.trim()) return;
    setError("");
    try {
      await api.post("/api/retailers", { name: newName.trim() });
      setNewName(""); setShowAdd(false); fetchRetailers();
    } catch (err) { setError(err.response?.data?.error || "Failed"); }
  };

  const handleSaveEdit = async () => {
    setError("");
    try {
      await api.put(`/api/retailers/${editingRetailer._id}`, { name: editName, description: editDesc });
      setEditingRetailer(null); fetchRetailers();
    } catch (err) { setError(err.response?.data?.error || "Failed"); }
  };

  const toggleActive = async (retailer) => {
    try {
      await api.put(`/api/retailers/${retailer._id}`, { isActive: !retailer.isActive });
      fetchRetailers();
    } catch (err) { console.error(err); }
  };

  return (
    <div>
      <div className="tl-card">
        <div className="tl-page-header">
          <div>
            <h2 style={{ display: "inline" }}>Retailers</h2>
            {inactiveCount > 0 && (
              <button className="btn btn-link btn-sm text-decoration-none ms-2" style={{ color: "var(--tl-text-muted)" }} onClick={() => setShowInactive(!showInactive)}>
                {showInactive ? "hide inactive" : `show inactive (${inactiveCount})`}
              </button>
            )}
          </div>
          {canEdit && <button className="btn btn-tl-primary" onClick={() => { setShowAdd(true); setError(""); }}>+ Add Retailer</button>}
        </div>
      </div>

      <div className="tl-card tl-card-compact">
        <table className="tl-table">
          <thead><tr><th>Retailer Name</th><th>Description</th><th style={{ width: "100px" }}>Status</th></tr></thead>
          <tbody>
            {filtered.map((r) => (
              <tr key={r._id} onClick={() => { setEditingRetailer(r); setEditName(r.name); setEditDesc(r.description || ""); setError(""); }} style={{ opacity: r.isActive ? 1 : 0.5 }}>
                <td><span className="name-link">{r.name}</span></td>
                <td className="text-muted" style={{ fontSize: "13px" }}>{r.description || "—"}</td>
                <td><span className={`status-badge ${r.isActive ? "status-active" : "status-discontinued"}`}>{r.isActive ? "Active" : "Inactive"}</span></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Add */}
      {showAdd && (
        <div className="modal d-block tl-modal" style={{ backgroundColor: "rgba(0,0,0,0.4)" }}>
          <div className="modal-dialog"><div className="modal-content">
            <div className="modal-header"><h5 className="modal-title">Add Retailer</h5><button className="btn-close" onClick={() => setShowAdd(false)} /></div>
            <div className="modal-body">
              {error && <div className="alert alert-danger py-2">{error}</div>}
              <label className="tl-form-label">Retailer Name</label>
              <input className="form-control tl-form-control" value={newName} onChange={(e) => setNewName(e.target.value)} onKeyDown={(e) => e.key === "Enter" && handleAdd()} autoFocus />
            </div>
            <div className="modal-footer">
              <button className="btn btn-tl-secondary" onClick={() => setShowAdd(false)}>Cancel</button>
              <button className="btn btn-tl-primary" onClick={handleAdd}>Save Changes</button>
            </div>
          </div></div>
        </div>
      )}

      {/* Edit */}
      {editingRetailer && (
        <div className="modal d-block tl-modal" style={{ backgroundColor: "rgba(0,0,0,0.4)" }}>
          <div className="modal-dialog"><div className="modal-content">
            <div className="modal-header"><h5 className="modal-title">{canEdit ? "Edit" : "View"} Retailer</h5><button className="btn-close" onClick={() => setEditingRetailer(null)} /></div>
            <div className="modal-body">
              {error && <div className="alert alert-danger py-2">{error}</div>}
              <div className="mb-3">
                <label className="tl-form-label">Retailer Name</label>
                {canEdit ? <input className="form-control tl-form-control" value={editName} onChange={(e) => setEditName(e.target.value)} autoFocus />
                : <div>{editingRetailer.name}</div>}
              </div>
              <div className="mb-3">
                <label className="tl-form-label">Description</label>
                {canEdit ? <input className="form-control tl-form-control" value={editDesc} onChange={(e) => setEditDesc(e.target.value)} />
                : <div>{editingRetailer.description || "—"}</div>}
              </div>
              <div className="mb-3">
                <label className="tl-form-label">Status</label>
                {canEdit ? (
                  <div>
                    <button className={`btn btn-sm ${editingRetailer.isActive ? "btn-outline-danger" : "btn-outline-success"}`} style={{ borderRadius: "8px" }} onClick={() => { toggleActive(editingRetailer); setEditingRetailer(null); }}>
                      {editingRetailer.isActive ? "Mark Inactive" : "Mark Active"}
                    </button>
                    <span className="text-muted small ms-2">{editingRetailer.isActive ? "Selectable in forms" : "Hidden from dropdowns"}</span>
                  </div>
                ) : <div><span className={`status-badge ${editingRetailer.isActive ? "status-active" : "status-discontinued"}`}>{editingRetailer.isActive ? "Active" : "Inactive"}</span></div>}
              </div>
            </div>
            <div className="modal-footer">
              {canEdit ? (
                <><button className="btn btn-tl-secondary" onClick={() => setEditingRetailer(null)}>Cancel</button><button className="btn btn-tl-primary" onClick={handleSaveEdit}>Save Changes</button></>
              ) : <button className="btn btn-tl-secondary" onClick={() => setEditingRetailer(null)}>Close</button>}
            </div>
          </div></div>
        </div>
      )}
    </div>
  );
};

export default RetailersPage;
